import { PremiumFeatureBadge } from '@src/components/PremiumBadge/PremiumFeatureBadge';
import { useEffect, useRef, type ReactNode } from 'react';
import { useAppSelector } from '@src/state/hooks';
import { useLocation } from 'react-router-dom';
import { CardDecoration } from '@src/theme/Decoration';
import { useTheme } from '@src/theme/useTheme';
import { ChevronDownIcon } from '@src/components/icons/ChevronDownIcon';
import './OrganizerSection.css';

/** Native disclosure preserves keyboard support and keeps form state when collapsed. */
export function OrganizerSection({
  mobile,
  gameKey,
  title,
  premium = false,
  premiumFeature,
  icon,
  id,
  children,
  onToggle,
  defaultOpen,
}: {
  mobile: boolean;
  gameKey: string;
  title: ReactNode;
  premium?: boolean;
  premiumFeature?: string;
  icon: ReactNode;
  id: string;
  children: ReactNode;
  onToggle?: (open: boolean) => void;
  defaultOpen?: boolean;
}) {
  const location = useLocation();
  const { season } = useTheme();
  const sectionRef = useRef<HTMLElement | null>(null);
  const userId = useAppSelector(state => state.session.user?.id);
  const storageKey = `ss:game-panel:${userId ?? 'anonymous'}:${gameKey}:${id}`;
  const choices = useRef(new Map<string, boolean>());
  useEffect(() => {
    let saved = choices.current.get(storageKey);
    if (saved === undefined) {
      try {
        const value = sessionStorage.getItem(storageKey);
        if (value === 'open' || value === 'closed') saved = value === 'open';
      } catch { /* Storage is optional. */ }
    }
    const section = sectionRef.current;
    if (!(section instanceof HTMLDetailsElement)) return;
    const linked = location.hash === `#${id}`;
    section.open = linked || (saved ?? defaultOpen ?? false);
    if (!linked) return;
    const frame = requestAnimationFrame(() => section.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, [storageKey, defaultOpen, id, location.key, location.hash]);
  function rememberToggle() {
    const section = sectionRef.current;
    if (!(section instanceof HTMLDetailsElement)) return;
    const next = !section.open;
    choices.current.set(storageKey, next);
    try { sessionStorage.setItem(storageKey, next ? 'open' : 'closed'); } catch { /* Storage is optional. */ }
  }
  return (
    <details
      id={id}
      ref={(node) => {
        sectionRef.current = node;
      }}
      className={`organizer-section${mobile ? ' organizer-section--mobile' : ''}`}
      onToggle={(event) => onToggle?.(event.currentTarget.open)}
      data-testid={`organizer-section-${id}`}
    >
      <summary onClick={rememberToggle}>
        {season !== 'egypt' && season !== 'candy' && id !== 'draw' && <CardDecoration variant={id === 'invite' ? 2 : 10} />}
        <span className="organizer-section__icon">
          {icon}

        </span>
        <span className="organizer-section__title">{title}</span>
        {premium && premiumFeature && <PremiumFeatureBadge feature={premiumFeature} />}
        <ChevronDownIcon className="organizer-section__chevron" />
      </summary>
      <div className="organizer-section__body">{children}</div>
    </details>
  );
}
